// CRM 2.0 › See all notifications (M1-F8, design §8.3), opened from "See all" in the header bell.
// Any logged-in user; the server returns only the user's own CRM 2.0 bell rows of the last 30 days.
// Filter All / Unread, Mark all read, Load more (older than the last row shown). A row click works as in the bell:
// it marks the row read, then opens View CRM Details for its booking (users who can open CRM 2.0)
// or shows its full text in place.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Button from "../../Subcomponents/Button";
import { ListHeader } from "../../Components/Crm2/ui/ListParts";
import Crm2BookingDetails from "../../Components/Crm2/Notifications/Crm2BookingDetails";
import {
  LOAD_ERROR_TEXT, NO_ROWS_TEXT, announceCrm2NotificationsChanged, fullTime, opensBooking, timeAgo,
} from "../../Components/Crm2/Notifications/notificationParts";
import {
  getCrm2Notifications, getCrm2UnreadCount, markAllCrm2NotificationsRead, markCrm2NotificationRead,
} from "../../Services/Crm2Notifications";
import { errorTost, successTost } from "../../Services/Toast";
import tbl from "../../Components/Crm2/Settings/Crm2Settings.module.css";
import css from "./Crm2NotificationsPage.module.css";

const FILTERS = [["all", "All"], ["unread", "Unread"]];
const btnSecondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const warn = (what, err, extra = {}) => console.warn(`[crm2][Crm2NotificationsPage] ${what}`, { ...extra, status: err?.response?.status, error: err?.message });

function Crm2NotificationsPage() {
  const user = useSelector((state) => state.Auth.user);
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null); // row showing its full text
  const [booking, setBooking] = useState(null); // allotmentPaymentId for View CRM Details
  const gen = useRef(0); // a newer list load makes older answers stale

  const loadCount = useCallback(() => {
    getCrm2UnreadCount()
      .then((d) => setUnread(Number(d?.count) || 0))
      .catch((err) => warn("unread count failed", err));
  }, []);

  useEffect(() => {
    const my = ++gen.current;
    setRows([]);
    setOpenId(null);
    setHasMore(false);
    setFailed(false);
    setLoading(true);
    loadCount();
    getCrm2Notifications({ days: 30, unread: filter === "unread" })
      .then((d) => {
        if (gen.current !== my) return;
        setRows(d?.rows || []);
        setHasMore(!!d?.hasMore);
      })
      .catch((err) => {
        warn("list failed", err, { filter });
        if (gen.current === my) setFailed(true);
      })
      .finally(() => {
        if (gen.current === my) setLoading(false);
      });
  }, [filter, loadCount]);

  // Older rows than the last one shown (rows made at the same moment come again and are dropped here),
  // so reading rows in the Unread list never shifts what comes next.
  const loadMore = async () => {
    if (loading || !rows.length) return;
    const my = gen.current;
    const before = rows[rows.length - 1].created_at;
    setLoading(true);
    try {
      const d = await getCrm2Notifications({ days: 30, unread: filter === "unread", before });
      if (gen.current !== my) return;
      setRows((rs) => {
        const have = new Set(rs.map((r) => r.crm2MessageId));
        return [...rs, ...(d?.rows || []).filter((r) => !have.has(r.crm2MessageId))];
      });
      setHasMore(!!d?.hasMore);
    } catch (err) {
      warn("load more failed", err, { filter, before });
      if (gen.current === my) errorTost("Couldn't load more CRM 2.0 notifications. Please try again.");
    } finally {
      if (gen.current === my) setLoading(false);
    }
  };

  const onRow = (row) => {
    if (!row.readAt) {
      const at = Date.now();
      setRows((rs) => rs.map((r) => (r.crm2MessageId === row.crm2MessageId ? { ...r, readAt: at } : r)));
      setUnread((c) => Math.max(0, c - 1));
      markCrm2NotificationRead(row.crm2MessageId)
        .then(() => announceCrm2NotificationsChanged())
        .catch((err) => {
          warn("mark read failed", err, { crm2MessageId: row.crm2MessageId });
          loadCount();
        });
    }
    if (opensBooking(row, user)) setBooking(row.allotmentPaymentId);
    else setOpenId((id) => (id === row.crm2MessageId ? null : row.crm2MessageId));
  };

  const markAll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await markAllCrm2NotificationsRead();
      const at = Date.now();
      setRows((rs) => rs.map((r) => (r.readAt ? r : { ...r, readAt: at })));
      setUnread(0);
      if (filter === "unread") setHasMore(false); // nothing unread is left to load
      announceCrm2NotificationsChanged();
      successTost("All CRM 2.0 notifications are marked read.");
    } catch (err) {
      warn("mark all read failed", err);
      errorTost("Couldn't mark the notifications read. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const emptyText = filter === "unread" ? "No unread CRM 2.0 notifications in the last 30 days." : NO_ROWS_TEXT;

  return (
    <div className={css.page}>
      <div>
        <p className={css.title}>CRM 2.0 notifications</p>
        <p className={css.sub}>Your CRM 2.0 notifications of the last 30 days. Click one to mark it read and open it.</p>
      </div>
      <div className={css.tabs} role="tablist" aria-label="Show">
        {FILTERS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={filter === key} className={`${css.tab} ${filter === key ? css.tabOn : ""}`} onClick={() => setFilter(key)}>
            {label}
          </button>
        ))}
      </div>
      <ListHeader count={unread} label="unread" addLabel={busy ? "Marking…" : "Mark all read"} onAdd={unread > 0 ? markAll : undefined} />
      {loading && !rows.length ? (
        <div className={tbl.empty}>Loading…</div>
      ) : failed ? (
        <div className={tbl.empty}>{LOAD_ERROR_TEXT}</div>
      ) : !rows.length ? (
        <div className={tbl.empty}>{emptyText}</div>
      ) : (
        <div className={tbl.tableCard}>
          <table className={tbl.table}>
            <thead>
              <tr>
                <th>Notification</th>
                <th>Unit</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const expanded = openId === r.crm2MessageId;
                const inPlace = !opensBooking(r, user);
                return (
                  <tr key={r.crm2MessageId} className={css.row} onClick={() => onRow(r)}>
                    <td>
                      {/* the row's keyboard target; its click reaches the row */}
                      <button type="button" className={css.rowBtn} aria-expanded={inPlace ? expanded : undefined}>
                        <span className={css.titleLine}>
                          {!r.readAt && <span className={css.dot} aria-hidden="true" />}
                          {!r.readAt && <span className={css.srOnly}>Unread: </span>}
                          <span className={r.readAt ? css.titleRead : css.titleUnread}>{r.title || "CRM 2.0 notification"}</span>
                        </span>
                        {r.body && <span className={expanded ? css.bodyFull : css.bodyShort}>{r.body}</span>}
                      </button>
                    </td>
                    <td>{r.unitLabel || "—"}</td>
                    <td className={css.when}>
                      {timeAgo(r.created_at)}
                      <span className={css.muted}>{fullTime(r.created_at)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && rows.length > 0 && (
        <div className={css.more}>
          <Button style={btnSecondary} btnName={loading ? "Loading…" : "Load more"} disable={loading} clicked={loadMore} />
        </div>
      )}
      {booking && <Crm2BookingDetails key={booking} allotmentPaymentId={booking} onClose={() => setBooking(null)} />}
    </div>
  );
}

export default Crm2NotificationsPage;
